import { useFormik } from "formik";
import moment from "moment";
import React, { useEffect } from "react";
import { Col, Row } from "react-bootstrap";
import { STATUS_DROPDOWN } from "../../../developmentContent/dropdown";
import { mergeClass } from "../../../resources/utils/helper";
import { AddBlogSchema } from "../../../schemas/AddBlogSchema";
import Button from "../../atoms/Button/index";
import { Input } from "../../atoms/Input/Input";
import ModalSkeleton from "../../atoms/ModalSkeleton/index";
import DropDown from "../../molecules/DropDown/DropDown";
import UploadImageBoxNew from "../../molecules/UploadImageBoxNew/UploadImageBoxNew/index";
import QuillInput from "../../organisms/QuillInput";
import classes from "./BlogsModal.module.css";

export const BlogsModal = ({
  showModal = false,
  setShowModal = () => {},
  loading = false,
  data,
  handleSubmit = () => {},
}) => {
  const formik = useFormik({
    initialValues: {
      title: "",
      description: "",
      status: null,
      createdAt: moment().format("YYYY-MM-DD"),
      thumbnailImage: null,
      bannerImage: null,
    },
    validationSchema: AddBlogSchema,
    onSubmit: (values) => {
      handleSubmit(values);
    },
  });

  useEffect(() => {
    if (data && showModal) {
      formik.setValues({
        title: data?.title || "",
        thumbnailImage: data?.thumbnailImage || data?.image || null,
        bannerImage: data?.bannerImage || (data?.image && !data?.thumbnailImage ? data?.image : null),
        description: data?.description || "",
        createdAt: data?.createdAt
          ? moment(data?.createdAt).format("YYYY-MM-DD")
          : data?.date
          ? moment(data?.date).format("YYYY-MM-DD")
          : moment().format("YYYY-MM-DD"),
        status: {
          label: data?.isActive ? "Activate" : "Deactivate",
          value: data?.isActive ? true : false,
        },
      });
    } else if (!data && showModal) {
      formik.resetForm();
    }
  }, [data, showModal]);

  return (
    <ModalSkeleton
      size="xl"
      show={showModal}
      setShow={setShowModal}
      modalStyles={classes.modal}
      header={`${data ? "Edit Blog" : "Add Blog"}`}
      headerClass={classes.header}
      showCloseIcon
      headerHeading={classes.heading}
    >
      <div className={classes.mainDiv}>
        <form onSubmit={formik.handleSubmit}>
          <Row className="g-4">
            {/* Left Column: Metadata and Images */}
            <Col lg={5} md={12} className={classes.leftColumn}>
              <div>
                <Input
                  label={"Title"}
                  value={formik.values.title}
                  setValue={(val) => formik.setFieldValue("title", val)}
                  errorText={formik.touched.title && formik.errors.title}
                  placeholder={"Blog Title"}
                />
              </div>

              <div>
                <Input
                  type="date"
                  label={"Publish Date"}
                  value={formik.values.createdAt}
                  setValue={(val) => formik.setFieldValue("createdAt", val)}
                  errorText={formik.touched.createdAt && formik.errors.createdAt}
                  placeholder={"Select Date"}
                />
              </div>

              {/* Images Section */}
              <div className={classes.imageUploadGrid}>
                {/* Blog Thumbnail Image */}
                <div className={classes.imageUploadCard}>
                  <UploadImageBoxNew
                    containerClass={classes.thumbnailBoxContainer}
                    uploadImageBox={classes.uploadBoxStyle}
                    hideDeleteIcon={true}
                    state={formik.values.thumbnailImage}
                    setter={(value) => formik.setFieldValue("thumbnailImage", value)}
                    onDelete={() => formik.setFieldValue("thumbnailImage", null)}
                    imgClass={classes.thumbnailImagePreview}
                    label={"Blog Thumbnail Image"}
                    subLabel={"Square image used on the blog listing page."}
                    errorText={formik.touched.thumbnailImage && formik.errors.thumbnailImage}
                  />
                </div>

                {/* Blog Banner Image */}
                <div className={classes.imageUploadCard}>
                  <UploadImageBoxNew
                    containerClass={classes.bannerBoxContainer}
                    uploadImageBox={classes.uploadBoxStyle}
                    hideDeleteIcon={true}
                    state={formik.values.bannerImage}
                    setter={(value) => formik.setFieldValue("bannerImage", value)}
                    onDelete={() => formik.setFieldValue("bannerImage", null)}
                    imgClass={classes.bannerImagePreview}
                    label={"Blog Banner Image"}
                    subLabel={"Wide rectangular image used as the banner/header on the blog detail page."}
                    errorText={formik.touched.bannerImage && formik.errors.bannerImage}
                  />
                </div>
              </div>

              {data?.slug && (
                <div>
                  <DropDown
                    className={mergeClass(
                      classes.dropDownContainer,
                      classes.customContainerClass
                    )}
                    options={STATUS_DROPDOWN?.filter((e) => e?.value !== "all")}
                    setValue={(val) => formik.setFieldValue("status", val)}
                    customStyle={{ minWidth: "180px" }}
                    value={formik.values.status}
                    label={"Status"}
                    placeholder={"Select Status"}
                    labelClassName={classes.myLabel}
                    errorText={formik.touched.status && formik.errors.status}
                  />
                </div>
              )}
            </Col>

            {/* Right Column: Rich Text Content */}
            <Col lg={7} md={12} className={classes.rightColumn}>
              <QuillInput
                label={"Blog Description"}
                setValue={(val) => formik.setFieldValue("description", val)}
                value={formik.values.description}
                placeholder={"Write blog content here..."}
                errorText={
                  formik.touched.description && formik.errors.description
                }
              />
            </Col>

            {/* Bottom Actions */}
            <Col md={12}>
              <div className={classes.buttonDiv}>
                <Button
                  variant="secondary"
                  onClick={() => setShowModal(false)}
                  disabled={loading}
                  type="button"
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  label={loading ? "Loading..." : data?.slug ? "Edit Blog" : "Save Blog"}
                  type={"submit"}
                  disabled={loading}
                />
              </div>
            </Col>
          </Row>
        </form>
      </div>
    </ModalSkeleton>
  );
};
